import {
  type Faro,
  FetchTransport,
  getWebInstrumentations,
  initializeFaro,
  type Instrumentation,
  type TransportItem
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

import { DEFAULT_IGNORED_COLLECTOR_URLS } from '../ignoredUrls';
import { scrubPII } from '../pii';
import { resolveNoddiDualTldUrl } from '../resolveNoddiDualTldUrl';
import { type Tracking, tracking } from '../tracking/tracking';
import { FaroShipper } from './faroShipper';

export { FaroShipper, type FaroApi } from './faroShipper';

export interface InitFaroOptions {
  // App identifier shown in Grafana (e.g. "noddi-customer", "my-lovable-app").
  // Maps to the OTLP resource attribute `service.name`, which the Alloy
  // `zendos_labels` transform promotes to the canonical `app` / `service` label —
  // the same key troncos backends emit. This is the one REQUIRED attribute.
  appName: string;
  // Alloy `otelcol.receiver.faro` collector URL (e.g. https://telemetry.noddi.co).
  // For Noddi dual-TLD collectors (`telemetry.noddi.co|no`), `initFaro` rewrites
  // the host to match the page TLD via `resolveNoddiDualTldUrl` so CORS works on
  // both app.noddi.co and app.noddi.no (and brand `.no` hosts). Other URLs as-is.
  url: string;
  // Non-secret app key sent as the `x-api-key` header (validated by the ztrapi
  // Traefik middleware). Safe to ship in browser JS; real origin control is the
  // server-side CORS allow-list.
  apiKey?: string;
  // App release/version (e.g. a git SHA or semver). Maps to `service.version`
  // → canonical `version` label, matching troncos' `service.version`. Put it on
  // the Faro `app` meta (resource attribute) — a session/super-property would only
  // be a log-record attribute, which `zendos_labels` (resource context) ignores.
  version?: string;
  // Environment ("production", "development", …). Maps to `deployment.environment`
  // → canonical `env` label, matching troncos' `deployment.environment`.
  environment?: string;
  // Commit SHA → Faro `app.gitHash` (OTLP resource attribute). Typically the
  // host's commit env var (Netlify `COMMIT_REF`). See `netlifyDeployMeta`.
  gitHash?: string;
  // Deploy namespace → Faro `app.namespace` → `service.namespace` (canonical
  // label). Typically the host's site name (Netlify `SITE_NAME`).
  namespace?: string;
  // Unique deploy/bundle id → Faro `app.bundleId` (OTLP resource attribute).
  // Typically the host's deploy id (Netlify `DEPLOY_ID`).
  bundleId?: string;
  // Runtime / deployment platform marker (e.g. "netlify") set as an extra
  // attribute on the Faro `app` meta → OTLP resource. Use to filter or group
  // telemetry by hosting platform in Grafana.
  runtime?: string;
  // Non-resource deploy metadata set as Faro session attributes — rides every
  // event for the session (searchable log-record attributes, NOT promoted to
  // labels by `zendos_labels`). See `netlifyDeployMeta` for the Netlify mapping.
  sessionAttributes?: Record<string, string>;
  // Master switch. Pass `false` on local dev so machines don't ship RUM.
  // When false, `initFaro` is a no-op and returns `undefined`.
  enabled?: boolean;
  // Fraction of sessions to record (0–1). Default 1.0.
  sessionSampleRate?: number;
  // PII scrubbing for outgoing payloads. `true` (default) uses the built-in
  // email + Norwegian-phone rules; pass a custom `beforeSend` to override.
  scrubPii?: boolean | ((item: TransportItem) => TransportItem | null);
  // Include Faro's default web instrumentations (fetch, console, errors,
  // unhandled rejections, web-vitals). Default true.
  webInstrumentations?: boolean;
  // Extra instrumentations to append — e.g. `new ReactIntegration()` from
  // `@grafana/faro-react` for React apps.
  instrumentations?: Instrumentation[];
  // Extra collector/third-party URLs Faro must not turn into resource events
  // or OTEL spans. The Faro collector URL is ignored automatically; we also
  // ignore OpenPanel / Mixpanel / Sentry by default.
  ignoreUrls?: Array<string | RegExp>;
  // Origins the browser MAY inject `traceparent`/`tracestate` into to enable
  // OpenTelemetry distributed tracing — auto fetch/XHR spans plus W3C context
  // propagation, so a browser request links to the Traefik → Django → worker
  // spans as one Tempo trace. Spans ride the same Faro OTLP transport and
  // auto-correlate with the RUM session via `trace_id`. MUST be limited to
  // first-party backend URLs you control whose CORS allow-list accepts the
  // header (string = exact match, RegExp = substring/pattern); never include
  // analytics/CDN/third-party origins. Empty (the default) disables tracing —
  // no `traceparent` is ever injected.
  tracePropagationUrls?: (string | RegExp)[];
  // Dispatcher to register the resulting `FaroShipper` on. Defaults to the
  // shared `tracking` singleton.
  tracking?: Tracking;
}

// Initializes Grafana Faro RUM and registers it as a shipper on the dispatcher.
// Call BEFORE other global error handlers (e.g. `Sentry.init`) so Faro's
// instrumentation is in place first. Returns the `Faro` instance, or `undefined`
// when disabled.
//
// React apps: pass `instrumentations: [new ReactIntegration()]` (from the
// optional `@grafana/faro-react` peer). Non-React apps omit it.
export function initFaro(options: InitFaroOptions): Faro | undefined {
  const {
    appName,
    url,
    apiKey,
    version,
    environment,
    gitHash,
    namespace,
    bundleId,
    runtime,
    sessionAttributes,
    enabled = true,
    sessionSampleRate = 1.0,
    scrubPii = true,
    webInstrumentations = true,
    instrumentations = [],
    tracePropagationUrls = [],
    ignoreUrls = [],
    tracking: dispatcher
  } = options;

  const collectorUrl = resolveNoddiDualTldUrl(url);

  if (!enabled || !collectorUrl) {
    return undefined;
  }

  const beforeSend = scrubPii === true ? scrubPII : scrubPii === false ? undefined : scrubPii;

  // Only enable tracing when there is at least one origin to propagate into;
  // an empty allow-list would emit spans the backend can't join and risks
  // leaking `traceparent` nowhere useful.
  const tracingInstrumentations =
    tracePropagationUrls.length > 0
      ? [new TracingInstrumentation({ instrumentationOptions: { propagateTraceHeaderCorsUrls: tracePropagationUrls } })]
      : [];

  // Build the app meta, omitting undefined fields so the OTLP resource only
  // carries meaningful attributes (name, version, env, gitHash, runtime, etc.).
  const appMeta: Record<string, string | undefined> = {
    name: appName,
    version,
    environment,
    gitHash,
    namespace,
    bundleId,
    runtime
  };
  const app = Object.fromEntries(Object.entries(appMeta).filter(([, v]) => v !== undefined)) as Parameters<
    typeof initializeFaro
  >[0]['app'];

  const faro = initializeFaro({
    transports: [new FetchTransport({ url: collectorUrl, apiKey })],
    // Faro `app` meta → OTLP resource attributes (service.name / service.version /
    // deployment.environment / service.namespace / gitHash / bundleId / runtime) →
    // canonical zendos labels + extra attributes for filtering in Grafana.
    app,
    sessionTracking: { enabled: true, samplingRate: sessionSampleRate },
    // Default web instrumentations emit `faro.performance.resource` for every
    // fetch/XHR. On a table/calendar/map that is one collector POST storm per
    // page (telemetry.noddi.co). Keep navigation + web-vitals + errors; do not
    // mirror each API/analytics call as its own RUM event. Console.info/warn
    // from the app and libraries would do the same — leave console capture off.
    trackResources: false,
    ignoreUrls: [...DEFAULT_IGNORED_COLLECTOR_URLS, ...ignoreUrls],
    instrumentations: [
      ...(webInstrumentations ? getWebInstrumentations({ captureConsole: false }) : []),
      ...tracingInstrumentations,
      ...instrumentations
    ],
    beforeSend
  });

  // Deploy metadata (e.g. Netlify context/branch/build id) as session attributes:
  // not resource attributes, but ride every event for the session.
  if (sessionAttributes && Object.keys(sessionAttributes).length > 0) {
    const session = faro.api.getSession();
    faro.api.setSession({ ...session, attributes: { ...session?.attributes, ...sessionAttributes } });
  }

  const target = dispatcher ?? tracking;
  target.registerShipper(new FaroShipper(faro));
  return faro;
}
